import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { authApi, ApiError, setAuthFailureHandler, getLastRefreshTime, setLastRefreshTime } from '../services/api';
import { broadcastAuthEvent, onAuthEvent, cleanup as cleanupAuthBroadcast } from '../utils/auth-broadcast';
import type { User, UserPreferences, SignInInput, SignUpInput } from '../types';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  signIn: (input: SignInInput) => Promise<void>;
  signUp: (input: SignUpInput) => Promise<{ userId: string }>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
  clearError: () => void;
  updatePreferences: (prefs: { [K in keyof UserPreferences]?: UserPreferences[K] | null }) => Promise<void>;
  updateProfile: (fields: {
    firstName?: string;
    lastName?: string;
    timezone?: string;
  }) => Promise<void>;
  /** Activate one of the user's connected providers' pictures (provider slug). */
  updateProfilePicture: (source: string) => Promise<void>;
  changePassword: (fields: { currentPassword: string; newPassword: string }) => Promise<{ success: true }>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchingRef = useRef(false);

  useEffect(() => {
    setAuthFailureHandler(() => {
      setUser(null);
      broadcastAuthEvent('logout');
    });
    return () => setAuthFailureHandler(null);
  }, []);

  const checkAuth = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const response = await authApi.me();
      if (response.success && response.user) {
        setUser(response.user);
        setLastRefreshTime(Date.now());
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      fetchingRef.current = false;
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    return () => { fetchingRef.current = false; };
  }, [checkAuth]);

  // Cross-tab auth synchronization
  useEffect(() => {
    const unsubscribe = onAuthEvent((type) => {
      if (type === 'login') {
        checkAuth();
      } else {
        setUser(null);
      }
    });
    return () => {
      unsubscribe();
      cleanupAuthBroadcast();
    };
  }, [checkAuth]);

  const signIn = useCallback(async (input: SignInInput) => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await authApi.signIn(input);
      if (response.success && response.user) {
        setUser(response.user);
        setLastRefreshTime(Date.now());
        broadcastAuthEvent('login');
      } else {
        throw new Error(response.error || 'Sign in failed');
      }
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'An error occurred during sign in';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signUp = useCallback(async (input: SignUpInput) => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await authApi.signUp(input);
      if (response.success) {
        // Return userId - user needs to sign in after signup
        return { userId: (response as { userId?: string }).userId || '' };
      } else {
        throw new Error(response.error || 'Sign up failed');
      }
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'An error occurred during sign up';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await authApi.signOut();
    } finally {
      setUser(null);
      setLastRefreshTime(0);
      broadcastAuthEvent('logout');
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const response = await authApi.refresh();
      if (response.success && response.user) {
        // /auth/refresh omits fields that /auth/me?expand=preferences includes
        // (notably `preferences`). Merge so omitted fields survive.
        const refreshed = response.user;
        setUser((prev) => (prev ? { ...prev, ...refreshed } : refreshed));
        setLastRefreshTime(Date.now());
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Proactive token refresh: interval timer + tab-visibility listener
  const isLoggedIn = !!user;
  useEffect(() => {
    if (!isLoggedIn) return;

    const REFRESH_INTERVAL_MS = 12 * 60 * 1000; // 12 minutes
    const VISIBILITY_THRESHOLD_MS = 10 * 60 * 1000; // 10 minutes

    const doRefresh = async () => {
      try {
        const response = await authApi.refresh();
        if (response.success && response.user) {
          const refreshed = response.user;
          setUser((prev) => (prev ? { ...prev, ...refreshed } : refreshed));
          setLastRefreshTime(Date.now());
        }
      } catch {
        // Silently ignore — reactive refresh on 401 will handle logout if needed
      }
    };

    // Periodic refresh every 12 min (3 min before 15-min expiry)
    const intervalId = setInterval(doRefresh, REFRESH_INTERVAL_MS);

    // Refresh when returning to a backgrounded tab after >10 min
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const elapsed = Date.now() - getLastRefreshTime();
        if (elapsed >= VISIBILITY_THRESHOLD_MS) {
          doRefresh();
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isLoggedIn]);

  const updatePreferences = useCallback(async (
    prefs: { [K in keyof UserPreferences]?: UserPreferences[K] | null },
  ) => {
    const updated = await authApi.updatePreferences(prefs);
    setUser((prev) => prev ? { ...prev, preferences: updated } : prev);
  }, []);

  const updateProfile = useCallback(async (
    fields: {
      firstName?: string;
      lastName?: string;
      timezone?: string;
    },
  ) => {
    const { updated } = await authApi.updateProfile(fields);
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...updated };
      if (updated.extendedProfile) {
        next.extendedProfile = { ...(prev.extendedProfile ?? {}), ...updated.extendedProfile };
      }
      return next;
    });
  }, []);

  const updateProfilePicture = useCallback(async (source: string) => {
    const { updated } = await authApi.updateProfile({ activeProfilePictureSource: source });
    setUser((prev) => {
      if (!prev) return prev;
      // Backend copies the chosen source's URL onto avatarUrl; fall back to the
      // URL we already know from profilePictures in case the response omits it.
      const localUrl = prev.profilePictures?.find((p) => p.source === source)?.url;
      return {
        ...prev,
        ...updated,
        activeProfilePictureSource: source,
        avatarUrl: updated.avatarUrl ?? localUrl ?? prev.avatarUrl,
      };
    });
  }, []);

  const changePassword = useCallback(
    (fields: { currentPassword: string; newPassword: string }) =>
      authApi.changePassword(fields),
    [],
  );

  const value: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    signIn,
    signUp,
    signOut,
    refresh,
    clearError,
    updatePreferences,
    updateProfile,
    updateProfilePicture,
    changePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
